/** Applied to programmatic `<caption>` when the same title is already visible above the table. */
export const FG_TABLE_SR_ONLY_CAPTION_CLASS = 'fg-table-sr-only-caption';

const CAPTION_MAX_LEN = 500;

const PREVIOUS_SIBLING_CAPTION_TAGS = new Set([
  'p',
  'div',
  'h1',
  'h2',
  'h3',
  'h4',
  'h5',
  'h6',
  'strong',
  'span',
]);

function captionTextFromPreviousSiblings(table: Element): string {
  let el: Element | null = table.previousElementSibling;
  while (el) {
    const tag = el.tagName.toLowerCase();
    if (PREVIOUS_SIBLING_CAPTION_TAGS.has(tag)) {
      const text = el.textContent?.replace(/\s+/g, ' ').trim() ?? '';
      if (text.length > 0) {
        return text.length > CAPTION_MAX_LEN ? `${text.slice(0, CAPTION_MAX_LEN)}…` : text;
      }
    }
    el = el.previousElementSibling;
  }
  return '';
}

function promoteTheadHeaderCellsToTh(table: Element, doc: Document) {
  const thead = table.querySelector(':scope > thead');
  if (!thead) {
    return;
  }
  const firstRow = thead.querySelector('tr:first-of-type');
  if (!firstRow) {
    return;
  }
  firstRow.querySelectorAll(':scope > td').forEach((td) => {
    const th = doc.createElement('th');
    while (td.firstChild) {
      th.appendChild(td.firstChild);
    }
    for (const attr of Array.from(td.attributes)) {
      th.setAttribute(attr.name, attr.value);
    }
    th.setAttribute('scope', 'col');
    td.replaceWith(th);
  });
}

function ensureTableCaption(table: Element, doc: Document) {
  if (table.querySelector(':scope > caption')) {
    return;
  }
  if (table.getAttribute('aria-label')?.trim() || table.getAttribute('aria-labelledby')?.trim()) {
    return;
  }
  const captionText = captionTextFromPreviousSiblings(table);
  if (!captionText) {
    return;
  }
  const caption = doc.createElement('caption');
  caption.className = FG_TABLE_SR_ONLY_CAPTION_CLASS;
  caption.textContent = captionText;
  table.insertBefore(caption, table.firstChild);
}

/**
 * Improves data-table semantics for publisher HTML: `<caption>` from a nearby title block and
 * `thead` header cells upgraded from `td` to `th scope="col"`.
 */
export function enhanceTablesInHtml(html: string): string {
  if (typeof document === 'undefined' || !/<table[\s>]/i.test(html)) {
    return html;
  }

  const parser = new DOMParser();
  const doc = parser.parseFromString(`<div data-fg-root="1">${html}</div>`, 'text/html');
  const root = doc.querySelector('[data-fg-root="1"]');
  if (!root) {
    return html;
  }

  root.querySelectorAll('table').forEach((table) => {
    ensureTableCaption(table, doc);
    promoteTheadHeaderCellsToTh(table, doc);
  });

  return root.innerHTML;
}
